import React, { useEffect, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { MenuGroup, Section, ButtonItem, SkeletonHeadingItem, SkeletonItem } from '@atlaskit/menu';

import { useSessionData, useIsLicensed, useIsGuest } from '@confluence/session-data';

import type { Participant } from '../presenceTypes';

import type { PresenceMenuProps } from './PresenceMenu';
import { PresenceMenuItem } from './PresenceMenuItem';
import { fetchPresenceUsersGuestStatus } from './fetchPresenceUsersGuestStatus';

const DEFAULT_PARTICIPANTS_TO_DISPLAY = 10;
const DEFAULT_PARTICIPANTS_TO_REVEAL = 25;

const i18n = defineMessages({
	activeNowTitle: {
		id: 'team-presence.presence-menu.active.title',
		defaultMessage:
			'{count, plural, =0 {Active now ({count})} one {Active now ({count})} other {Active now ({count})}}',
		description:
			'The text is shown as a title of a section inside of a menu. It describes the number of users actively on the page. {count} will be a literal number, like 0, 1, 2, 3, etc...',
	},
	viewingNowTitle: {
		id: 'team-presence.presence-menu.viewing.title',
		defaultMessage:
			'{count, plural, =0 {Viewing now ({count})} one {Viewing now ({count})} other {Viewing now ({count})}}',
		description:
			'The text is shown as a title of a section inside of a menu. It describes the number of users actively viewing the page. {count} will be a literal number, like 0, 1, 2, 3, etc...',
	},
	editingNowTitle: {
		id: 'team-presence.presence-menu.editing.title',
		defaultMessage:
			'{count, plural, =0 {Editing now ({count})} one {Editing now ({count})} other {Editing now ({count})}}',
		description:
			'The text is shown as a title of a section inside of a menu. It describes the number of users actively editing on the page. {count} will be a literal number, like 0, 1, 2, 3, etc...',
	},
	loadMore: {
		id: 'team-presence.presence-menu.load-more',
		defaultMessage: 'Load More',
		description: 'Load more text',
	},
});

const SkeletonItems = ({ max, next }: { max: number; next: number }) => {
	const items = Array.from({
		length: Math.min(max, next),
	});

	return items.map((_, index) => (
		<SkeletonItem key={`skeleton-item-${index}`} testId="skeleton-item" hasAvatar isShimmering />
	));
};

export type PresenceMenuWithGuestProps = Omit<PresenceMenuProps, 'onLoadMoreClick' | 'testId'>;

export const PresenceMenuWithGuest = ({
	contentId,
	contentType,
	contentMode,
	participants,
	totalParticipants,
}: PresenceMenuWithGuestProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isLoggedIn } = useSessionData();
	const isLicensed = useIsLicensed();
	const isGuest = useIsGuest();

	const [isInitial, setIsInitial] = React.useState<boolean>(true);
	const [isLoadMoreClicked, setIsLoadMoreClicked] = React.useState<boolean>(false);
	const [isLoadingMore, setIsLoadingMore] = React.useState<boolean>(false);

	const guestStatus = useRef<Map<string, boolean>>(new Map());

	const participantsWithGuestStatus = participants.filter((it) => guestStatus.current.has(it.id));
	const participantsWithoutGuestStatus = participants.filter(
		(it) => !guestStatus.current.has(it.id),
	);
	const hasParticipantsWithoutGuestStatus = participantsWithoutGuestStatus.length > 0;

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'presenceMenu',
				attributes: {
					contentId,
					contentType,
					contentMode,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, contentId, contentType, contentMode]);

	useEffect(() => {
		const fetchGuestStatus = async ({ next }: { next: number }) => {
			setIsLoadingMore(true);

			const userIds: string[] = [];
			const anonymousIds: string[] = [];

			participantsWithoutGuestStatus.slice(0, next).forEach((participant) => {
				if (!guestStatus.current.has(participant.id)) {
					if (participant.userId === 'unidentified') {
						anonymousIds.push(participant.id);
					} else {
						userIds.push(
							// When the userId is in the format ari:cloud:identity::user/{userId}
							// we need to extract the userId part
							participant.userId.replace('ari:cloud:identity::user/', ''),
						);
					}
				}
			});

			if (userIds.length > 0) {
				if (!isLoggedIn || !isLicensed || isGuest) {
					// If the user is not logged in, not licensed, or is a guest ... we don't show their guest status
					// so we just set the guest status to false for these participants.
					userIds.forEach((userId) => {
						const userARI = `ari:cloud:identity::user/${userId}`;

						participants
							.filter((p) => p.userId === userId || p.userId === userARI)
							.forEach((p) => guestStatus.current.set(p.id, false));
					});
				} else {
					// Whiteboards does not deduplicate users, we might want to force a
					// unique on the list of participants to query
					const users = await fetchPresenceUsersGuestStatus(userIds);

					users.forEach((user) => {
						const userId = user.userId;
						const userARI = `ari:cloud:identity::user/${userId}`;

						participants
							.filter((p) => p.userId === userId || p.userId === userARI)
							.forEach((p) => guestStatus.current.set(p.id, user.isGuest));
					});
				}
			}

			// If the participant is unidentified, we set their guest status to false
			// as anonymous users can't be guests
			anonymousIds.forEach((id) => guestStatus.current.set(id, false));

			setIsLoadingMore(false);
			setIsInitial(false);
		};

		if ((isInitial || isLoadMoreClicked) && !isLoadingMore) {
			setIsLoadMoreClicked(false);

			void fetchGuestStatus({
				next: isInitial ? DEFAULT_PARTICIPANTS_TO_DISPLAY : DEFAULT_PARTICIPANTS_TO_REVEAL,
			});
		}
	}, [
		isGuest,
		isInitial,
		isLicensed,
		isLoadMoreClicked,
		isLoadingMore,
		isLoggedIn,
		participants,
		participantsWithoutGuestStatus,
	]);

	const handleOnLoadMoreClicked = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'presenceMenuWithGuestLoadMore',
				source: 'presenceMenuWithGuest',
				attributes: {
					shown: participantsWithGuestStatus.length,
					total: totalParticipants,
				},
			},
		}).fire();

		setIsLoadMoreClicked(true);
	}, [createAnalyticsEvent, participantsWithGuestStatus, totalParticipants]);

	const getHeaderTitle = useCallback(
		(count: number) => {
			if (contentMode === 'edit' && (contentType === 'page' || contentType === 'blogpost')) {
				return formatMessage(i18n.editingNowTitle, { count });
			} else if (contentMode === 'view' && (contentType === 'page' || contentType === 'blogpost')) {
				return formatMessage(i18n.viewingNowTitle, { count });
			} else {
				return formatMessage(i18n.activeNowTitle, { count });
			}
		},
		[contentMode, contentType, formatMessage],
	);

	return (
		<MenuGroup maxHeight={480} minWidth={360} maxWidth={360}>
			{participantsWithGuestStatus.length > 0 ? (
				<Section
					isScrollable
					title={getHeaderTitle(totalParticipants)}
					testId="presence-menu-group-guest"
				>
					{participantsWithGuestStatus.map((participant: Participant, index) => {
						const transformedParticipant = {
							...participant,
							isGuest: guestStatus.current.get(participant.id) ?? false,
						};

						return (
							<PresenceMenuItem
								contentId={contentId}
								contentType={contentType}
								contentMode={contentMode}
								key={`menu-item-${transformedParticipant.id.slice(6)}`}
								participant={transformedParticipant}
								isCurrentUser={transformedParticipant.isCurrentUser || index === 0}
							/>
						);
					})}
					{hasParticipantsWithoutGuestStatus && !isLoadingMore && (
						<ButtonItem onClick={handleOnLoadMoreClicked}>
							{formatMessage(i18n.loadMore)}
						</ButtonItem>
					)}
					{hasParticipantsWithoutGuestStatus && isLoadingMore && (
						<SkeletonItems
							max={DEFAULT_PARTICIPANTS_TO_REVEAL}
							next={participantsWithoutGuestStatus.length}
						/>
					)}
				</Section>
			) : (
				<Section>
					<SkeletonHeadingItem isShimmering />
					<SkeletonItems
						max={DEFAULT_PARTICIPANTS_TO_DISPLAY}
						next={participantsWithoutGuestStatus.length}
					/>
				</Section>
			)}
		</MenuGroup>
	);
};
